import {z} from "zod";

// Zod allows us to ensure that the response type meets our expectations, at runtime.
// This is better than asserting that the response type is of a certain type, because
// if the response type changes, the code will break at runtime, rather than silently.
// This gives us a better developer experience, and allows us to catch bugs earlier.

const LessonCardSchema = z.object({
  headline: z.string(),
  type: z.literal("lesson"),
  lessonId: z.number(),
  title: z.string(),
  artistName: z.string(),
  hasVideo: z.boolean(),
  imageUrl: z.string(),
  levelDifficulty: z.string(),
  difficultyIdentifier: z.union([z.literal("rookie"), z.literal("intermediate"), z.literal("advanced")]),
});

const SongCardSchema = z.object({
  headline: z.string(),
  type: z.literal("song"),
  songId: z.number(),
  title: z.string(),
  artistName: z.string(),
  hasVideo: z.boolean(),
  imageUrl: z.string(),
  numberOfArrangements: z.number(),
});

const PlaylistChildCardSchema = z.discriminatedUnion('type', [LessonCardSchema, SongCardSchema]);

const PlaylistCardSchema = z.object({
  headline: z.string(),
  type: z.literal("playlist"),
  playlistId: z.number(),
  title: z.string(),
  imageUrl: z.string(),
  subtext: z.string(),
  difficultyIdentifier: z.union([z.literal("rookie"), z.literal("intermediate"), z.literal("advanced"), z.literal("mixed")]),
  cards: z.array(z.discriminatedUnion('type', [LessonCardSchema, SongCardSchema])),
});

const MusicHomeGroupSchema = z.object({
  id: z.number(),
  isFeatured: z.boolean().optional(),
  title: z.string(),
  cards: z.array(z.union([PlaylistCardSchema, LessonCardSchema, SongCardSchema])),
});

// A simple union does not work here. We need to use a discriminated union.
// This approach to discriminated unions is the current approach by the Zod team,
// but an alternative approach is being considered.
// See https://github.com/colinhacks/zod/issues/2106
const MusicHomeCardSchema = z.discriminatedUnion('type', [PlaylistCardSchema, LessonCardSchema, SongCardSchema]);

export const MusicHomeSchema = z.object({
  groups: z.array(MusicHomeGroupSchema)
});

export type MusicHomeResponse = z.infer<typeof MusicHomeSchema>;
export type MusicHomeGroup = z.infer<typeof MusicHomeGroupSchema>;
export type MusicHomeCard = z.infer<typeof MusicHomeCardSchema>;
export type PlaylistChildCard = z.infer<typeof PlaylistChildCardSchema>;
export type MusicHomePlaylistCard = z.infer<typeof PlaylistCardSchema>;
export type MusicHomeLessonCard = z.infer<typeof LessonCardSchema>;
export type MusicHomeSongCard = z.infer<typeof SongCardSchema>;
